import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';

interface ProtectedPath {
  path: string;
  pattern?: RegExp;
  requiresAuth?: (request: HttpRequest<unknown>) => boolean;
}

const PROTECTED_PATHS: ProtectedPath[] = [
  { path: '/Users/Me/Listings' },
  { path: '/Images' },
  { path: '/v1.1/billing' },
  {
    path: '/Listings',
    pattern: /\/Listings(?:\/[^/]+)?$/,
    requiresAuth: (request) => {
      const router = inject(Router);
      const currentUrl = router.url;

      // Always require auth for POST requests
      if (request.method === 'POST') {
        return true;
      }

      // For GET requests, check the route
      return (
        currentUrl?.startsWith('/account/listing') ||
        currentUrl?.startsWith('/your-tesla') ||
        currentUrl?.startsWith('/account') ||
        false
      );
    },
  },
];

export const AuthInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const auth = inject(AuthService);

  // Extract path from full URL for pattern matching
  const urlObj = new URL(request.url);
  const pathToCheck = urlObj.pathname;

  // Check if the request URL requires authentication
  const requiresAuth = PROTECTED_PATHS.some((protectedPath) => {
    if (protectedPath.pattern) {
      const matches = protectedPath.pattern.test(pathToCheck);
      if (matches && protectedPath.requiresAuth) {
        const needsAuth = protectedPath.requiresAuth(request);
        return needsAuth;
      }
      return matches;
    }
    return pathToCheck.includes(protectedPath.path);
  });

  // Skip authentication for public routes
  if (!requiresAuth) {
    return next(request);
  }

  return auth.getAccessTokenSilently().pipe(
    switchMap((token) => {
      if (token) {
        const clonedRequest = request.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
          },
        });
        return next(clonedRequest);
      }
      return next(request);
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        auth.logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      }
      return throwError(() => error);
    })
  );
};
